import * as React                           from 'react';

import cc                                   from 'classcat';

import { ArrowDownVector                  } from 'src/components/vector/arrow-down';
import { ArrowUpVector                    } from 'src/components/vector/arrow-up';
import { ThreeDotsVector                  } from 'src/components/vector/three-dots';
import { CircleQuestionVector             } from 'src/components/vector/circle-question';
import { DangerVector                     } from 'src/components/vector/danger';

import { IconButton                       } from 'src/components/common/icon-button';
import { Typography                       } from 'src/components/common/typography';
import { DateLabel                        } from 'src/components/common/date-label';
import { LabeledContent                   } from 'src/components/common/labeled-content';

import { Status,
         StatusVariants                   } from 'src/components/features/main/inspection-list/status';
import { QMMsgList                        } from 'src/components/features/main/inspection-list/qmmsg-list';

import { WrapperContainer,
         Wrapper,
         Container,
         ExpandButtonWrapper,
         InspectionHeaderContainer,
         InspectionDateContainer,
         InspectionStatusContainer,
         InspectionContentContainer,
         InspectionTypeContainer,
         InspectionQMMsgContainer,
         InspectionNumberContainer,
         InspectionGroupContentContainer,
         InspectionDivider,
         Danger,
         DangerIcon,
         DangerContent,
         ClampedLines,
         InspectionVariants,
         inspection_variants              } from 'src/components/features/main/inspection-list/inspection/ui';

import { Urls                             } from 'src/providers/routing';

import * as types                           from 'src/services/api/types';
import { InspectionState,
         InspectionType                   } from 'src/services/api/types/inspection';

import { useAppDispatch,
         storeApi                         } from 'src/store';



type Props = {
  variant: InspectionVariants;
  inspection: types.inspection.Inspection;
  currentAccount: types.auth.AccountWithTokens;
  onThreeDotTap: (inspection: types.inspection.Inspection) => void;
  onQuestionTap: (inspection: types.inspection.Inspection) => void;
  onDangerTap: (inspection: types.inspection.Inspection) => void;
}
export const Inspection: React.FC<Props> = ({
  variant,
  inspection,
  currentAccount,
  onThreeDotTap,
  onQuestionTap,
  onDangerTap,
}) => {
  const lookAndFeel = inspection_variants.get(variant)!;
  const [expanded, setExpanded] = React.useState<boolean>(false);

  const dispatch = useAppDispatch();

  const { ClientAdmin, Contractor, Stpk } = types.auth.AccountRole;

  const isStpk = currentAccount.role === Stpk;
  const isClientAdminOrStpk = [ClientAdmin, Stpk].includes(currentAccount.role);
  const isQMsgAccessAllowed = currentAccount.isClaimStatus;
  const isContractor = currentAccount.role === Contractor;
  const isAwaitingStatus = [
    types.inspection.InspectionStatusCode.AWAITING_CONFIRMATION,
    types.inspection.InspectionStatusCode.AWAITING_REVIEW,
    types.inspection.InspectionStatusCode.AWAITING_VERIFICATION,
  ].includes(inspection.status.code);

  const { MECHANICAL_ENGINEERING_PART, PROCESSED_SHEET, UNPROCESSED_SHEET } = types.inspection.ProductTypeKolpino;

  const qmetIds: number[] = [];
  const pieceIds: string[] = [];
  const groupAttIds: string[] = [];
  const carriageIds: string[] = [];
  const specificationIds: string[] = [];
  const kolpinoMechanicalEngineeringPartIds: string[] = [];
  const kolpinoProcessedSheetIds: string[] = [];
  const kolpinoUnprocessedSheetIds: string[] = [];
  const defects: string[] = [];
  const productTypes: string[] = [];
  const qmetIdsByGuid: Map<string, number[]> = new Map<string, number[]>();
  const pieceIdsByGuid: Map<string, string[]> = new Map<string, string[]>();
  const groupAttIdsByGuid: Map<string, string[]> = new Map<string, string[]>();
  const carriageIdsByGuid: Map<string, string[]> = new Map<string, string[]>();
  const specificationIdsByGuid: Map<string, string[]> = new Map<string, string[]>();
  const kolpinoMechanicalEngineeringPartIdsByGuid: Map<string, string[]> = new Map<string, string[]>();
  const kolpinoProcessedSheetIdsByGuid: Map<string, string[]> = new Map<string, string[]>();
  const kolpinoUnprocessedSheetIdsByGuid: Map<string, string[]> = new Map<string, string[]>();

  inspection.inspectionDefects.forEach((defect) => {
    if (defect.defectTitle !== null)
    {
      defects.push(defect.defectTitle);
    }

    (defect.pieces || []).forEach((piece) => {
      const isKolpino = piece.productTypeKolpinoId !== null;
      const productTypeKolpino = piece.productTypeKolpinoId;

      /** Если грязные вагоны */
      if (piece.carriage !== null && piece.carriage !== '')
      {
        const oldItems = carriageIdsByGuid.get(defect.guid) ?? [];
        const newItem = piece.carriage;

        carriageIds.push(newItem);
        carriageIdsByGuid.set(defect.guid, [ ...oldItems, newItem]);
      }

      /** Если сроки */
      if (piece.specificationNum !== null && piece.specificationNum !== '')
      {
        const oldItems = specificationIdsByGuid.get(defect.guid) ?? [];
        const newItem = piece.specificationNum;

        specificationIds.push(newItem);
        specificationIdsByGuid.set(defect.guid, [ ...oldItems, newItem]);
      }

      /** Если заводили по идентификационному номеру */
      if (!piece.isPiece && piece.qmetId !== null && piece.qmetId > 0)
      {
        const oldItems = qmetIdsByGuid.get(defect.guid) ?? [];
        const newItem = piece.qmetId;

        qmetIds.push(newItem);
        qmetIdsByGuid.set(defect.guid, [ ...oldItems, newItem]);
      }

      /** Если заводили не по идентификационному номеру, то нужно разделить на Колпино и все остальное */
      if (isKolpino)
      {
        switch (productTypeKolpino)
        {
          /** Если детали машиностроения */
          case MECHANICAL_ENGINEERING_PART:
            {
              const oldItems = kolpinoMechanicalEngineeringPartIdsByGuid.get(defect.guid) ?? [];
              const newItem = `${piece.piece}/${piece.qcNum}`;

              kolpinoMechanicalEngineeringPartIds.push(newItem);
              kolpinoMechanicalEngineeringPartIdsByGuid.set(defect.guid, [ ...oldItems, newItem]);
            }
            break;
          /** Если обработанный лист */
          case PROCESSED_SHEET:
            {
              const oldItems = kolpinoProcessedSheetIdsByGuid.get(defect.guid) ?? [];
              const newItem = `${piece.piece}/${piece.qcNum}`;

              kolpinoProcessedSheetIds.push(newItem);
              kolpinoProcessedSheetIdsByGuid.set(defect.guid, [ ...oldItems, newItem]);
            }
            break;
          /** Если не обработанный лист - аналогично катанке */
          case UNPROCESSED_SHEET:
            {
              const oldItems = kolpinoUnprocessedSheetIdsByGuid.get(defect.guid) ?? [];
              const newItemHeat = (piece.heat === null || piece.heat === '') ? 'Нет плавки' : piece.heat;
              const newItem = `${newItemHeat}/${piece.groupAtt}/${piece.qcNum}`;

              kolpinoUnprocessedSheetIds.push(newItem);
              kolpinoUnprocessedSheetIdsByGuid.set(defect.guid, [ ...oldItems, newItem]);
            }
            break;
        }
      }
      else
      {
        /** Если заводили по плавке/листу */
        if (piece.isPiece && piece.heat !== null && piece.piece !== null && piece.heat !== '' && piece.piece !== '' && (piece.groupAtt === null || piece.groupAtt === ''))
        {
          const oldItems = pieceIdsByGuid.get(defect.guid) ?? [];
          const newItem = `${piece.heat}/${piece.piece}`;

          pieceIds.push(newItem);
          pieceIdsByGuid.set(defect.guid, [ ...oldItems, newItem]);
        }

        /** Если катанка */
        if (piece.isPiece && piece.groupAtt !== null && piece.groupAtt !== '')
        {
          const oldItems = groupAttIdsByGuid.get(defect.guid) ?? [];
          const newItemHeat = (piece.heat === null || piece.heat === '') ? 'Нет плавки' : piece.heat;
          const newItem = `${newItemHeat}/${piece.groupAtt}/${piece.qcNum}`;

          groupAttIds.push(newItem);
          groupAttIdsByGuid.set(defect.guid, [ ...oldItems, newItem]);
        }
      }

      if ((piece.qmetData.productType ?? '') !== '' && !productTypes.includes(piece.qmetData.productType ?? ''))
      {
        productTypes.push(piece.qmetData.productType ?? '');
      }
    });
  });

  const isThreeDotsVisible = !isAwaitingStatus;
  const isQuestionButtonVisible = inspection.status.code === types.inspection.InspectionStatusCode.AWAITING_CONFIRMATION;

  const isQMMsgVisible = (isClientAdminOrStpk || isQMsgAccessAllowed) && !inspection.isGroup && inspection.qmmsgs.length > 0;

  const isNumberSignVisible = inspection.isEntryAccounting;
  const numberSignLabel = isContractor ? 'Без дефекта' : 'Входной учет';

  const isProductTypeVisible = productTypes.length > 0;

  const isNumberDefectVisible = !inspection.isGroup && !inspection.isEntryAccounting;
  const numberDefectLabel = defects;

  const isDefectsVisible = inspection.isGroup && !inspection.isEntryAccounting && !expanded;
  const defectsLabel = defects;

  const isCommonAllVisible = !inspection.isGroup || inspection.isEntryAccounting;
  const isQmetIdsAllVisible = isCommonAllVisible && qmetIds.length > 0;
  const isPieceIdsAllVisible = isCommonAllVisible && pieceIds.length > 0;
  const isGroupAttIdsAllVisible = isCommonAllVisible && groupAttIds.length > 0;
  const isCarriageIdsAllVisible = isCommonAllVisible && carriageIds.length > 0;
  const isSpecificationIdsAllVisible = isCommonAllVisible && specificationIds.length > 0;
  const isKolpinoMechanicalEngineeringPartIdsAllVisible = isCommonAllVisible && kolpinoMechanicalEngineeringPartIds.length > 0;
  const isKolpinoProcessedSheetIdsAllVisible = isCommonAllVisible && kolpinoProcessedSheetIds.length > 0;
  const isKolpinoUnprocessedSheetIdsAllVisible = isCommonAllVisible && kolpinoUnprocessedSheetIds.length > 0;

  const isDefectsListVisible = expanded && inspection.isGroup;

  const onInspectionAction = () => {
    const {
      DRAFT,
      INTERNAL,
      ERROR,
      NO_TRANS_DRAFT,
      NO_TRANS_ERROR,
      INTERNAL_NO_TRANS_COMPLETED
    } = types.inspection.InspectionStatusCode;

    const isDraft = inspection.status.code === DRAFT;
    const isInternal = inspection.status.code === INTERNAL;
    const isError = inspection.status.code === ERROR;
    const isNoTransDraft = inspection.status.code === NO_TRANS_DRAFT;
    const isNoTransError = inspection.status.code === NO_TRANS_ERROR;
    const isInternalNoTransCompleted = inspection.status.code === INTERNAL_NO_TRANS_COMPLETED;

    if (
      inspection.status != null
      &&
      (
        isDraft
        ||
        (isInternal && !inspection.isEntryAccounting)
        ||
        (isInternalNoTransCompleted && !inspection.isEntryAccounting)
        ||
        isError
        ||
        isNoTransDraft
        ||
        isNoTransError
      )
    )
    {
      Urls.InspectionEdit.build(inspection.guid, getInspectionState(inspection), false, false, false, false, false, false).navigate();
    }
    else
    {
      dispatch(storeApi.inspection.view.async.openForViewAsync({ viewGuid: inspection.guid }));
    }
  }

  return (
    <WrapperContainer lookAndFeel = { lookAndFeel }>
      <Wrapper lookAndFeel = { lookAndFeel }>
        <Container lookAndFeel = { lookAndFeel } onClick = { onInspectionAction }>

          { /** Заголовок карточки осмотра - временная метка, статус, кнопка вызова меню (три точки) */ }
          <InspectionHeaderContainer lookAndFeel = { lookAndFeel } isRightPadding = { isAwaitingStatus && !isQuestionButtonVisible }>
            <InspectionDateContainer lookAndFeel = { lookAndFeel }>
              <DateLabel variant = { lookAndFeel.dateLabelCreatedVariant } date = { new Date(inspection.timestampCreated) } />
              <DateLabel variant = { lookAndFeel.dateLabelInspectionVariant } date = { new Date(inspection.timestampInspection) } />
            </InspectionDateContainer>
            <InspectionStatusContainer lookAndFeel = { lookAndFeel }>
              <Status variant = { getStatusVariant(inspection.status.code) } label = { inspection.status.title } />
            </InspectionStatusContainer>
            {isThreeDotsVisible && (
              <IconButton
                variant = { lookAndFeel.threeDotButtonVariant }
                isTapAllowed
                onTap = { () => onThreeDotTap(inspection) }
              >
                <ThreeDotsVector />
              </IconButton>
            )}
            {isQuestionButtonVisible && (
              <IconButton
                variant = { lookAndFeel.questionButtonVariant }
                isTapAllowed
                onTap = { () => onQuestionTap(inspection) }
              >
                <CircleQuestionVector />
              </IconButton>
            )}
          </InspectionHeaderContainer>

          { /** Тело карточки осмотра */ }
          <InspectionContentContainer lookAndFeel = { lookAndFeel }>

            { /** Тип осмотра */}
            {inspection.inspectionType !== null && (
              <InspectionTypeContainer lookAndFeel = { lookAndFeel }>
                <Typography variant = { lookAndFeel.inspectionTypeTypographyVariant }>
                  { getInspectionTypeStr(inspection) }
                </Typography>
              </InspectionTypeContainer>
            )}

            { /** Плашка qmmsg с датой регистрации и статусом */ }
            {isQMMsgVisible && (
              <InspectionQMMsgContainer lookAndFeel = { lookAndFeel }>
                <QMMsgList
                  variant = { lookAndFeel.qmmsgListVariant }
                  list = { inspection.qmmsgs }
                  onJump = { (item) => Urls.QMMsgDetail.build('inspectionlist', item.id, true).navigate() }
                />
              </InspectionQMMsgContainer>
            )}

            { /** Плашка с номером осмотра и дефектом или признаком */ }
            <InspectionNumberContainer lookAndFeel = { lookAndFeel }>
              <LabeledContent variant = { lookAndFeel.labeledVariant } label = '№ осмотра'>
                <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                  { inspection.inspectionNum }
                </Typography>
              </LabeledContent>
              {isNumberSignVisible && (
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Признак'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    { numberSignLabel }
                  </Typography>
                </LabeledContent>
              )}
              {isNumberDefectVisible && (
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Дефект'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    { numberDefectLabel }
                  </Typography>
                </LabeledContent>
              )}
            </InspectionNumberContainer>

            { /** Вид продукции */ }
            {isProductTypeVisible && (
              <InspectionGroupContentContainer lookAndFeel = { lookAndFeel }>
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Вид продукции'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    { productTypes.join(', ') }
                  </Typography>
                </LabeledContent>
              </InspectionGroupContentContainer>
            )}

            { /** Плашка с дефектами (не список дефектов, а Дефекты и ниже через запятую перечисление) */ }
            {isDefectsVisible && (
              <InspectionGroupContentContainer lookAndFeel = { lookAndFeel }>
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Дефекты'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    { defectsLabel.join(', ') }
                  </Typography>
                </LabeledContent>
              </InspectionGroupContentContainer>
            )}

            { /** Список qmetId общий */ }
            {isQmetIdsAllVisible && (
              <InspectionGroupContentContainer lookAndFeel = { lookAndFeel }>
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Идентификационный №'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    <ClampedLines
                      lookAndFeel = { lookAndFeel }
                      className = { cc({ expanded }) }
                    >
                      { qmetIds.join(', ') }
                    </ClampedLines>
                  </Typography>
                </LabeledContent>
              </InspectionGroupContentContainer>
            )}

            { /** Список pieceId общий */ }
            {isPieceIdsAllVisible && (
              <InspectionGroupContentContainer lookAndFeel = { lookAndFeel }>
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Плавка / Партия / Ед'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    <ClampedLines
                      lookAndFeel = { lookAndFeel }
                      className = { cc({ expanded }) }
                    >
                      { pieceIds.join(', ') }
                    </ClampedLines>
                  </Typography>
                </LabeledContent>
              </InspectionGroupContentContainer>
            )}

            { /** Список groupAttIds общий */ }
            {isGroupAttIdsAllVisible && (
              <InspectionGroupContentContainer lookAndFeel = { lookAndFeel }>
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Плавка / Партия атт / Сертификат'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    <ClampedLines
                      lookAndFeel = { lookAndFeel }
                      className = { cc({ expanded }) }
                    >
                      { groupAttIds.join(', ') }
                    </ClampedLines>
                  </Typography>
                </LabeledContent>
              </InspectionGroupContentContainer>
            )}

            { /** Список carriageIds общий */ }
            {isCarriageIdsAllVisible && (
              <InspectionGroupContentContainer lookAndFeel = { lookAndFeel }>
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Вагон №'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    <ClampedLines
                      lookAndFeel = { lookAndFeel }
                      className = { cc({ expanded }) }
                    >
                      { carriageIds.join(', ') }
                    </ClampedLines>
                  </Typography>
                </LabeledContent>
              </InspectionGroupContentContainer>
            )}

            { /** Список specificationIds общий */ }
            {isSpecificationIdsAllVisible && (
              <InspectionGroupContentContainer lookAndFeel = { lookAndFeel }>
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Спецификация №'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    <ClampedLines
                      lookAndFeel = { lookAndFeel }
                      className = { cc({ expanded }) }
                    >
                      { specificationIds.join(', ') }
                    </ClampedLines>
                  </Typography>
                </LabeledContent>
              </InspectionGroupContentContainer>
            )}

            { /** Список isKolpinoMechanicalEngineeringPartIdsAllVisible общий */ }
            {isKolpinoMechanicalEngineeringPartIdsAllVisible && (
              <InspectionGroupContentContainer lookAndFeel = { lookAndFeel }>
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Лист / Сертификат'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    <ClampedLines
                      lookAndFeel = { lookAndFeel }
                      className = { cc({ expanded }) }
                    >
                      { kolpinoMechanicalEngineeringPartIds.join(', ') }
                    </ClampedLines>
                  </Typography>
                </LabeledContent>
              </InspectionGroupContentContainer>
            )}

            { /** Список isKolpinoProcessedSheetIdsAllVisible общий */ }
            {isKolpinoProcessedSheetIdsAllVisible && (
              <InspectionGroupContentContainer lookAndFeel = { lookAndFeel }>
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Лист / Сертификат'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    <ClampedLines
                      lookAndFeel = { lookAndFeel }
                      className = { cc({ expanded }) }
                    >
                      { kolpinoProcessedSheetIds.join(', ') }
                    </ClampedLines>
                  </Typography>
                </LabeledContent>
              </InspectionGroupContentContainer>
            )}

            { /** Список isKolpinoUnprocessedSheetIdsAllVisible общий */ }
            {isKolpinoUnprocessedSheetIdsAllVisible && (
              <InspectionGroupContentContainer lookAndFeel = { lookAndFeel }>
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Плавка / Партия / Сертификат'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    <ClampedLines
                      lookAndFeel = { lookAndFeel }
                      className = { cc({ expanded }) }
                    >
                      { kolpinoUnprocessedSheetIds.join(', ') }
                    </ClampedLines>
                  </Typography>
                </LabeledContent>
              </InspectionGroupContentContainer>
            )}

            { /** Список дефектов развернутый */ }
            {isDefectsListVisible && (
              <React.Fragment>
                {inspection.inspectionDefects.map((defect) => (
                  <InspectionGroupContentContainer key = { defect.guid } lookAndFeel = { lookAndFeel }>
                    <InspectionDivider lookAndFeel={lookAndFeel} />
                    <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Дефект'>
                      <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                        { defect.defectTitle }
                      </Typography>
                    </LabeledContent>
                    {qmetIdsByGuid.get(defect.guid) !== undefined && (
                      <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Идентификационный №'>
                        <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                          <ClampedLines
                            lookAndFeel = { lookAndFeel }
                            className = { cc({ expanded }) }
                          >
                            { qmetIdsByGuid.get(defect.guid)!.join(', ') }
                          </ClampedLines>
                        </Typography>
                      </LabeledContent>
                    )}
                    {pieceIdsByGuid.get(defect.guid) !== undefined && (
                      <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Плавка / Партия / Ед'>
                        <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                          <ClampedLines
                            lookAndFeel = { lookAndFeel }
                            className = { cc({ expanded }) }
                          >
                            { pieceIdsByGuid.get(defect.guid)!.join(', ') }
                          </ClampedLines>
                        </Typography>
                      </LabeledContent>
                    )}
                    {groupAttIdsByGuid.get(defect.guid) !== undefined && (
                      <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Плавка / Партия атт / Сертификат'>
                        <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                          <ClampedLines
                            lookAndFeel = { lookAndFeel }
                            className = { cc({ expanded }) }
                          >
                            { groupAttIdsByGuid.get(defect.guid)!.join(', ') }
                          </ClampedLines>
                        </Typography>
                      </LabeledContent>
                    )}
                    {carriageIdsByGuid.get(defect.guid) !== undefined && (
                      <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Вагон №'>
                        <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                          <ClampedLines
                            lookAndFeel = { lookAndFeel }
                            className = { cc({ expanded }) }
                          >
                            { carriageIdsByGuid.get(defect.guid)!.join(', ') }
                          </ClampedLines>
                        </Typography>
                      </LabeledContent>
                    )}
                    {specificationIdsByGuid.get(defect.guid) !== undefined && (
                      <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Спецификация №'>
                        <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                          <ClampedLines
                            lookAndFeel = { lookAndFeel }
                            className = { cc({ expanded }) }
                          >
                            { specificationIdsByGuid.get(defect.guid)!.join(', ') }
                          </ClampedLines>
                        </Typography>
                      </LabeledContent>
                    )}
                    {kolpinoMechanicalEngineeringPartIdsByGuid.get(defect.guid) !== undefined && (
                      <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Лист / Сертификат'>
                        <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                          <ClampedLines
                            lookAndFeel = { lookAndFeel }
                            className = { cc({ expanded }) }
                          >
                            { kolpinoMechanicalEngineeringPartIdsByGuid.get(defect.guid)!.join(', ') }
                          </ClampedLines>
                        </Typography>
                      </LabeledContent>
                    )}
                    {kolpinoProcessedSheetIdsByGuid.get(defect.guid) !== undefined && (
                      <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Лист / Сертификат'>
                        <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                          <ClampedLines
                            lookAndFeel = { lookAndFeel }
                            className = { cc({ expanded }) }
                          >
                            { kolpinoProcessedSheetIdsByGuid.get(defect.guid)!.join(', ') }
                          </ClampedLines>
                        </Typography>
                      </LabeledContent>
                    )}
                    {kolpinoUnprocessedSheetIdsByGuid.get(defect.guid) !== undefined && (
                      <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Плавка / Партия / Сертификат'>
                        <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                          <ClampedLines
                            lookAndFeel = { lookAndFeel }
                            className = { cc({ expanded }) }
                          >
                            { kolpinoUnprocessedSheetIdsByGuid.get(defect.guid)!.join(', ') }
                          </ClampedLines>
                        </Typography>
                      </LabeledContent>
                    )}
                  </InspectionGroupContentContainer>
                ))}
              </React.Fragment>
            )}

            { /** Инициатор */ }
            {expanded && (
              <InspectionGroupContentContainer lookAndFeel={lookAndFeel}>
                <InspectionDivider lookAndFeel={lookAndFeel} />
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Инициатор'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    { inspection.userFio }
                  </Typography>
                </LabeledContent>
              </InspectionGroupContentContainer>
            )}

            { /** Предприятие (показывается только для СТПК) */ }
            {expanded && isStpk && getInspectionEnterpriseTitle(inspection) !== '-' && (
              <InspectionGroupContentContainer lookAndFeel={lookAndFeel}>
                <InspectionDivider lookAndFeel={lookAndFeel} />
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Предприятие'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    { getInspectionEnterpriseTitle(inspection) }
                  </Typography>
                </LabeledContent>
              </InspectionGroupContentContainer>
            )}

            { /** Автор (показывается только для СТПК) */ }
            {expanded && isStpk && getInspectionAuthor(inspection) !== '-' && (
              <InspectionGroupContentContainer lookAndFeel={lookAndFeel}>
                <InspectionDivider lookAndFeel={lookAndFeel} />
                <LabeledContent variant = { lookAndFeel.labeledVariant } label = 'Автор'>
                  <Typography variant = { lookAndFeel.labeledTypographyVariant }>
                    { getInspectionAuthor(inspection) }
                  </Typography>
                </LabeledContent>
              </InspectionGroupContentContainer>
            )}
          </InspectionContentContainer>
        </Container>
        <ExpandButtonWrapper lookAndFeel = { lookAndFeel }>
          <IconButton
            variant = { lookAndFeel.expandButtonVariant }
            isTapAllowed
            onTap = { () => setExpanded(!expanded) }
          >
            { expanded ? <ArrowUpVector /> : <ArrowDownVector /> }
          </IconButton>
        </ExpandButtonWrapper>
      </Wrapper>
      {!inspection.__isTrans && (
        <Danger lookAndFeel = { lookAndFeel } onClick = { () => onDangerTap(inspection) }>
          <DangerIcon lookAndFeel = { lookAndFeel }>
            <DangerVector />
          </DangerIcon>
          <DangerContent lookAndFeel = { lookAndFeel }>
            <Typography variant = { lookAndFeel.dangerHeaderTypographyVariant }>
              Возникла проблема отправки осмотра
            </Typography>
            <Typography variant = { lookAndFeel.dangerMessageTypographyVariant }>
              Требуется отправка — зайдите в Осмотр
            </Typography>
          </DangerContent>
        </Danger>
      )}
    </WrapperContainer>
  );
};


const getStatusVariant = (status: types.inspection.InspectionStatusCode): StatusVariants => {
  const {
    AWAITING_CONFIRMATION,
    AWAITING_REVIEW,
    AWAITING_VERIFICATION,
    TAKE_TO_WORK,
    COMPLETED,
    INTERNAL,
    RETURN_FOR_REVISION,
    REVOCATION,
    ERROR,
    DRAFT,
    NO_TRANS_DRAFT,
    NO_TRANS_ERROR,
    NO_TRANS_COMPLETED,
    INTERNAL_NO_TRANS_COMPLETED,
  } = types.inspection.InspectionStatusCode;

  switch (status)
  {
    case AWAITING_CONFIRMATION:
    case AWAITING_REVIEW:
    case AWAITING_VERIFICATION:
      return 'bluefill';
    case TAKE_TO_WORK:
    case COMPLETED:
    case INTERNAL:
      return 'blueoutline';
    case RETURN_FOR_REVISION:
    case REVOCATION:
      return 'redoutline';
    case ERROR:
    case NO_TRANS_DRAFT:
    case NO_TRANS_ERROR:
    case NO_TRANS_COMPLETED:
    case INTERNAL_NO_TRANS_COMPLETED:
      return 'redfill';
    case DRAFT:
      return 'gray';
    default:
      return 'gray';
  }
}

const getInspectionTypeStr = (inspection: types.inspection.Inspection): string => {
  const { REMARK, CLAIM, NOTIFICATION } = InspectionType;
  const { inspectionType } = inspection;
  let regDate = null;

  if (inspection.qmmsgs.length === 0)
  {
    if (inspectionType !== null && [CLAIM, NOTIFICATION].includes(inspectionType))
    {
      regDate = new Date(inspection.timestampInspection).toLocaleDateString();
    }
  }
  else
  {
    regDate = new Date(inspection.qmmsgs[0].timestampReg).toLocaleDateString();
  }

  switch (inspectionType)
  {
    case REMARK:
      return 'Замечание';
    case CLAIM:
      return regDate === null ? 'Претензия' :`Претензия от ${regDate}`;
    case NOTIFICATION:
      return regDate === null ? 'Уведомление' :`Уведомление от ${regDate}`;
  }

  return '';
}

const getInspectionState = (inspect: types.inspection.Inspection): InspectionState => {
  const {
    DRAFT,
    ERROR,
    NO_TRANS_DRAFT,
    NO_TRANS_ERROR,
    NO_TRANS_COMPLETED,
    INTERNAL_NO_TRANS_COMPLETED
  } = types.inspection.InspectionStatusCode;

  switch (inspect.status.code)
  {
    case DRAFT:
      return InspectionState.DRAFT;
    case ERROR:
      return InspectionState.ERROR;
    case NO_TRANS_DRAFT:
      return InspectionState.DRAFT;
    case NO_TRANS_ERROR:
      return InspectionState.ERROR;
    case NO_TRANS_COMPLETED:
    case INTERNAL_NO_TRANS_COMPLETED:
      return InspectionState.EDIT;
  }

  return InspectionState.EDIT;
}

const getInspectionEnterpriseTitle = (inspect: types.inspection.Inspection): string => {
  const enterpriseTitle = inspect.inspectionDefects
    .map((defect) => defect.complaint?.initiator?.enterpriseTitle ?? null)
    .filter((enterpriseTitle) => enterpriseTitle !== null)
    .join(', ');

  return enterpriseTitle === '' ? '-' : enterpriseTitle;
}

const getInspectionAuthor = (inspect: types.inspection.Inspection): string => {
  const authorFIO = inspect.inspectionDefects
    .map((defect) => defect.complaint?.initiator?.fio ?? null)
    .filter((fio) => fio !== null)
    .join(', ');

  return authorFIO === '' ? '-' : authorFIO;
}
